import type { MutableRefObject } from "react";
import React from "react";
import { cn } from "@rosette/ts/src/util/cn.js";
import type { LobbyStatus } from "@rosette/ts/src/business/game/controller/status/LobbyStatus.js";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import { Button } from "@rosette/web_components/src/ui/Button.js";
import Link from "next/link";
import { DISCORD_INVITE_URL } from "@rosette/ts/src/business/constants.js";
import Image from "next/image";
import discordImage from "@rosette/assets/src/icons/join_the_discord.svg";
import { LoadingSpinner } from "@rosette/web_components/src/icon/LoadingSpinner.js";
import { useGameClientControls } from "@/components/game/GameUIContext.js";
import { useCopy } from "@rosette/web_utils/src/useCopy.js";
import { MENU_BUTTON, MENU_PANEL_TITLE } from "@/components/game/menu/MenuLayout.js";
import { Card } from "@rosette/web_components/src/ui/Card.js";


interface LoadingMenuProps {
    contentRef: MutableRefObject<HTMLDivElement | null>;
    lobbyStatus: LobbyStatus;
}


export function LoadingMenu({ contentRef, lobbyStatus }: LoadingMenuProps) {
    const clientControls = useGameClientControls();

    let title = "Online Game";
    const gameMode = lobbyStatus.getGameMode();
    if (lobbyStatus.isGameUnknown()) {
        title = "Unknown Game";
    } else if (gameMode) {
        title = gameMode.getName() + " Game";
    }

    let url: string | null = null;
    let desc: string;
    let showLoadingSpinner = false;

    if (lobbyStatus.isGameUnknown()) {
        desc = "Your game could not be found.";
    } else if (!lobbyStatus.isConnected()) {
        desc = (lobbyStatus.hasEverConnected() ? "Reconnecting..." : "Connecting...");
        showLoadingSpinner = true;
    } else {
        if (gameMode === null) {
            desc = "Waiting for game to load...";
            showLoadingSpinner = true;
        } else if (gameMode === GameMode.ONLINE || gameMode === GameMode.RANKED) {
            desc = "Waiting for your opponent to join...";
            showLoadingSpinner = true;
        } else {
            desc = "Share the following link with a friend, so they can join!";
        }

        if (lobbyStatus.isRemote()) {
            const lobbyID = lobbyStatus.getLobbyID();
            url = window.location.protocol + "//" + window.location.host + "/game/" + lobbyID.get();
        }
    }

    const { copyText, doCopy } = useCopy(url, "Copy Link", "Copied Link");
    return (
        <div
            className="flex flex-col items-center w-full p-4"
            ref={contentRef}>

            <Card tone="menu" className="mb-6 max-w-[30rem] text-center">
                <h1 className={MENU_PANEL_TITLE}>{title}</h1>
                <p className="mb-0 text-2xl text-body">{desc}</p>

                {gameMode && gameMode === GameMode.FRIEND && url && (
                    <div className="flex flex-col items-stretch px-4">
                        <input
                            className={cn(
                                "grow mt-4 mb-4 py-[0.2em] px-[0.4em] rounded-[0.2em]",
                                "text-2xl text-muted bg-surface-neutral",
                            )}
                            type="text"
                            readOnly={true}
                            value={url} />

                        <Button tone="accent" size="lg" onClick={doCopy}>
                            {copyText}
                        </Button>
                    </div>
                )}

                {showLoadingSpinner && (
                    <LoadingSpinner className="mt-3" />
                )}
            </Card>

            {gameMode && gameMode === GameMode.ONLINE && (
                <Card tone="menu" className="mb-6 max-w-[30rem] text-center">
                    <p className="mt-0 mb-0 text-2xl text-body">
                        You can also
                        {" "}
                        <Link href={DISCORD_INVITE_URL}>
                            join our Discord
                        </Link>
                        {" "}
                        to find opponents to play!
                    </p>
                    <Link className="block mt-4" href={DISCORD_INVITE_URL}>
                        <Image
                            className="w-full max-w-[65%] mx-auto"
                            src={discordImage}
                            alt="Discord Logo" />
                    </Link>
                </Card>
            )}

            <Button
                size="lg"
                className={MENU_BUTTON}
                onClick={() => clientControls.abortGame()}>
                Exit
            </Button>
        </div>
    );
}
